import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { FolderService } from '../../../services/folder.service';
import { GalleryService } from '../../../services/gallery.service';
import { FolderBreadcrumbComponent } from '../../../shared/components/folder-breadcrumb/folder-breadcrumb.component';
import { FinderContentComponent } from '../finder-content/finder-content.component';

@Component({
  selector: 'app-finder',
  templateUrl: './finder.component.html',
  styleUrls: ['./finder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, FinderContentComponent, FolderBreadcrumbComponent],
})
export class FinderComponent implements OnInit, OnDestroy {
  private folderService = inject(FolderService);
  private galleryService = inject(GalleryService);
  private check = inject(ChangeDetectorRef);
  @Output() onSelectFolder = new EventEmitter();
  @Output() onSelectGallery = new EventEmitter();
  @Input() blockGallerySelection = false;
  @Input() allowFolderAndGalleryAsTarget = false;
  @Input() hideSubGalleries = false;
  @Input() currentFolder: FolderDto = undefined;
  @Input() currentGallery: GalleryDto = undefined;
  @Input() disableList: (FolderDto | GalleryDto)[] = [];
  @Input() filterOut: (FolderDto | GalleryDto)[] = [];
  @Input() reOpenFn: (...args) => void = undefined;
  @ViewChild('searchField', { static: false, read: ElementRef }) searchField: ElementRef<HTMLInputElement>;

  breadcrumbs: (FolderDto | GalleryDto)[] = [];
  allFolders: FolderDto[];
  allGalleries: GalleryDto[];
  noResults = false;
  searchTerm = '';
  searchForm = new FormGroup({
    search: new FormControl(''),
  });

  destroyed$ = new Subject<void>();

  async ngOnInit() {
    const [folders, galleries] = await Promise.all([
      firstValueFrom(this.folderService.getFolders()),
      firstValueFrom(this.galleryService.getAllGalleriesIncludingSubgalleries({ ignorePublicAccessLevel: true })),
    ]);
    this.allFolders = folders;
    this.allGalleries = galleries;

    if (this.currentGallery) {
      this.currentFolder = this.currentFolder || this.allFolders.find(f => f.id === this.currentGallery.folder);
      this.onSelectFolder.emit(this.currentFolder);
      this.onSelectGallery.emit(this.currentGallery);
    }

    this.searchForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.searchTerm = this.searchForm.value.search;
    });
    this.check.detectChanges();
  }

  upFolder() {
    if (!this.allFolders) return;
    if (!this.currentFolder) return;
    if (!this.currentFolder.parent_folder) this.selectFolder(undefined);
    else this.selectFolder(this.allFolders.find(f => f.id === this.currentFolder.parent_folder));
  }

  selectFolder(folder: FolderDto) {
    this.currentFolder = folder;
    this.onSelectFolder.emit(this.currentFolder);
    if (this.allowFolderAndGalleryAsTarget) {
      this.selectGallery(undefined);
    } else {
      this.onSelectGallery.emit(undefined);
    }
    this.calculateBreadcrumbs();
    this.check.detectChanges();
    return false;
  }

  selectGallery(gallery: GalleryDto) {
    if (gallery?.id === this.currentGallery?.id) {
      this.currentGallery = undefined;
    } else {
      this.currentGallery = gallery;
    }
    this.calculateBreadcrumbs();
    this.check.detectChanges();
    this.onSelectGallery.emit(this.currentGallery);
    return false;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private calculateBreadcrumbs() {
    const breadcrumbs = [];
    let folder = this.currentFolder;
    while (folder) {
      breadcrumbs.unshift(folder);
      folder = this.allFolders.find(f => f.id === folder.parent_folder);
    }
    if (this.currentGallery) {
      breadcrumbs.push(this.currentGallery);
    }
    this.breadcrumbs = breadcrumbs;
  }
}
